import { listen, qs, qsa } from "@fluorescent/dom";
import accordionsInit from "@/lib/accordions";
import socialShareInit from "@/lib/social-share";
import truncateWrapperInit from "@/lib/truncate-wrapper";
import {
  complementaryProductsInit,
  informationPopupInit,
  updateInventoryCounterFromHTML,
  optionButtonsInit,
  quantityInputInit,
  reviewsFormatter,
  siblingProductsInit,
} from "@/lib/product";
import { wrapIframes, wrapTables } from "@/lib/rte";

const selectors = {
  accordion: ".accordion",
  socialShareContainer: ".social-share",
  customOptionInputs: "[data-custom-option-input]",
  customOptionInputTargetsById: id => `[data-custom-option-target='${id}']`,
  description: ".product__description",
  nonSprRatingCountLink: ".product__rating-count-potential-link",
  optionButtons: "[data-option-buttons]",
  productReviews: "#shopify-product-reviews",
};

export default function productBlocks(container) {
  const { productRating } = container.dataset;

  const components = [
    ...accordionElsInit(qsa(selectors.accordion, container)),
    socialShareInit(qs(selectors.socialShareContainer, container)),
    complementaryProductsInit(container),
    informationPopupInit(container),
    quantityInputInit(container),
    siblingProductsInit(container),
    truncateWrapperInit(container),
  ];

  const events = [
    ...customOptionsInit(
      qsa(selectors.customOptionInputs, container),
      container
    ), // Adds listeners for each custom option, to sync input changes
  ];

  // Option-button listeners are tracked separately so they can be re-bound
  // after the variant picker is swapped in from a section response.
  let optionButtonListeners = optionButtonsInit(
    qsa(selectors.optionButtons, container)
  );

  const refreshOptionButtons = () => {
    optionButtonListeners.forEach(unsubscribe => unsubscribe());
    optionButtonListeners = optionButtonsInit(
      qsa(selectors.optionButtons, container)
    );
  };

  // Stock level indicator: apply the liquid-resolved current-variant state
  // (rendered into the counter's data-current-* attributes) to the live
  // element. Correct for any variant regardless of position — no dependency on
  // the variant array, which Liquid caps at 250. Variant changes re-render the
  // counter via the Section Rendering API swap in product.js. No-ops when the
  // block isn't present or Liquid rendered it hidden.
  updateInventoryCounterFromHTML(container, container);

  // Format iframes and tables in description
  formatDescription(qsa(selectors.description, container));

  // Format Shopify Product Reviews
  reviewsFormatter(qsa(selectors.productReviews, document), productRating);

  // Format non-SPR rating display
  formatNonSprRatingCount(qsa(selectors.nonSprRatingCountLink, container));

  const unload = () => {
    components.filter(Boolean).forEach(component => component.unload());
    events.forEach(unsubscribe => unsubscribe());
    optionButtonListeners.forEach(unsubscribe => unsubscribe());
  };

  return { unload, refreshOptionButtons };
}

const accordionElsInit = els => {
  const accordions = els.map(el => {
    const accordionOpen = el.classList.contains("accordion--open");
    const accordionParent = el.parentElement;
    const accordion = accordionsInit(el, { firstOpen: accordionOpen });

    if (
      accordionParent.classList.contains("rte--product") &&
      !accordionParent.classList.contains("accordion accordion--product")
    ) {
      el.classList.add("rte--product", "accordion--product");
    }

    return accordion;
  });
  return accordions;
};

const customOptionsInit = (els, container) => {
  const listeners = els.map(input => {
    const id = input.dataset.customOptionInput;
    const target = qs(selectors.customOptionInputTargetsById(id), container);
    const listener = listen(input, "change", e => {
      // Update the hidden input within the form, per type
      if (e.target.type === "checkbox") {
        target.checked = e.target.checked;
      } else {
        target.value = e.target.value;
      }
    });
    return listener;
  });
  return listeners;
};

const formatDescription = els => {
  if (!els.length) return;

  els.forEach(el => {
    wrapIframes(qsa("iframe", el));
    wrapTables(qsa("table", el));
  });
};

const formatNonSprRatingCount = els => {
  if (els.length && !qs(selectors.productReviews, document)) {
    // The rating count links to "#shopify-product-reviews" but
    // if that block doesn't exist we should remove the link
    els.forEach(el => el.removeAttribute("href"));
  }
};
